/**
 * TanStack Query hooks for Data Catalog Sync operations
 * Handles syncing discovery scan results into the PAP data catalog
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { post, del, isApiError } from '@/lib/api-client'
import { dataTableKeys } from './use-data-tables'
import type { CatalogSyncResult } from '@/types/data-table'

/**
 * Sync discovery scan results into the data catalog
 */
export function useSyncFromDiscovery() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: { scanId: string; applicationId: string }) =>
      post<CatalogSyncResult>('/data-tables/sync-from-discovery', data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: dataTableKeys.all })

      const parts: string[] = []
      if (result.tablesCreated > 0) parts.push(`${result.tablesCreated} tables created`)
      if (result.tablesUpdated > 0) parts.push(`${result.tablesUpdated} updated`)
      if (result.columnsCreated > 0) parts.push(`${result.columnsCreated} columns`)
      if (result.attributesCreated > 0)
        parts.push(`${result.attributesCreated} attributes`)

      toast.success(`Sync complete: ${parts.join(', ')}`)

      if (result.errors.length > 0) {
        toast.warning(`${result.errors.length} error(s) during sync`)
      }
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to sync from discovery'
      toast.error(message)
    },
  })
}

/**
 * Cleanup catalog data for a discovery connection
 */
export function useCleanupByConnection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (connectionId: string) =>
      del<{ deleted: boolean }>(`/data-tables/by-connection/${connectionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataTableKeys.all })
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to cleanup catalog data'
      toast.error(message)
    },
  })
}
